import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, ArrowLeft, User, Eye, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  technique: { label: 'Technique', color: 'bg-blue-100 text-blue-700' },
  training: { label: 'Training', color: 'bg-green-100 text-green-700' },
  strategy: { label: 'Strategy', color: 'bg-purple-100 text-purple-700' },
  fitness: { label: 'Fitness', color: 'bg-orange-100 text-orange-700' },
  gear: { label: 'Gear', color: 'bg-pink-100 text-pink-700' }
};

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const res = await axios.get(`${API}/blog/${slug}`);
      setArticle(res.data);
      // Update page title for SEO
      document.title = `${res.data.title} | Badminton Stats Blog`;
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Render markdown-like content to HTML
  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-bold text-gray-900 mt-8 mb-3">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-gray-800 mt-6 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('- **')) {
        elements.push(
          <li key={i} className="text-gray-700 leading-relaxed ml-4 list-disc" dangerouslySetInnerHTML={{
            __html: line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
          }} />
        );
      } else if (line.startsWith('- ')) {
        elements.push(
          <li key={i} className="text-gray-700 leading-relaxed ml-4 list-disc" dangerouslySetInnerHTML={{
            __html: line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
          }} />
        );
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(
          <p key={i} className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
            __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
          }} />
        );
      }
      i++;
    }
    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
        <p className="text-gray-500 text-lg">Article not found</p>
        <Button onClick={() => navigate('/blog')} variant="outline">Back to Blog</Button>
      </div>
    );
  }

  const catInfo = CATEGORY_LABELS[article.category] || { label: article.category, color: 'bg-gray-100 text-gray-700' };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-article-page">
      {/* Nav */}
      <nav className="w-full bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_blog-56/artifacts/ar7lcvoq_WhatsApp%20Image%202026-02-21%20at%2022.08.34.jpeg" alt="Logo" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold text-blue-600">Badminton Stats</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-sm text-gray-600 hover:text-blue-600 hidden sm:inline">Blog</Link>
            <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700 text-white">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-6 py-3">
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <ol className="flex items-center gap-1 flex-wrap">
            <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
            <li>/</li>
            <li><Link to="/blog" className="hover:text-blue-600">Blog</Link></li>
            <li>/</li>
            <li className="text-gray-900 font-medium truncate max-w-[200px]">{article.title}</li>
          </ol>
        </nav>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 pb-20">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Badge className={`${catInfo.color} text-xs`}>{catInfo.label}</Badge>
            <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="h-3 w-3" /> {article.read_time} min read</span>
            <span className="text-xs text-gray-400 flex items-center gap-1"><Eye className="h-3 w-3" /> {article.views} views</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4">{article.title}</h1>
          <p className="text-base text-gray-600 leading-relaxed">{article.excerpt}</p>
          <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
            <User className="h-4 w-4" />
            <span>{article.author}</span>
          </div>
        </header>

        {/* Content */}
        <div className="prose-container bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm">
          {renderContent(article.content)}
        </div>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Tag className="h-4 w-4 text-gray-400" />
            {article.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
          <h2 className="text-xl font-bold mb-2">Ready to improve your game?</h2>
          <p className="text-blue-100 mb-4 text-sm">Track matches, get AI coaching, and log your training — all free.</p>
          <Button onClick={() => navigate('/signup')} className="bg-white text-blue-600 hover:bg-blue-50 px-6 font-semibold">
            Start Tracking Free
          </Button>
        </div>

        {/* Back to blog */}
        <div className="mt-8">
          <Link to="/blog" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to all articles
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer className="w-full bg-white border-t border-gray-200 py-6 text-center text-sm text-gray-500">
        <Link to="/" className="hover:text-blue-600 mr-4">Home</Link>
        <Link to="/features" className="hover:text-blue-600 mr-4">Features</Link>
        <Link to="/blog" className="hover:text-blue-600 mr-4">Blog</Link>
        <Link to="/privacy" className="hover:text-blue-600 mr-4">Privacy</Link>
        <Link to="/terms" className="hover:text-blue-600">Terms</Link>
      </footer>
    </div>
  );
};

export default BlogArticle;
